/**
 * The single source of randomness in the application.
 *
 * ARCHITECTURAL INVARIANT: no module outside this file may call `Math.random()`. Every draw,
 * shuffle and tie-break comes from a seeded stream so that a test can replay an identical run.
 *
 * `fork(label)` gives an independent, deterministically-derived stream. Round composition and
 * effects animation must not share a stream, or adding a particle changes which item is drawn.
 */
export interface Rng {
  /** Uniform in [0, 1). */
  next(): number;
  /** Uniform integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  /** Uniform element. Throws on an empty array — an empty draw is always a bug, never a fallback. */
  pick<T>(items: readonly T[]): T;
  /** Fisher–Yates, returning a new array. */
  shuffle<T>(items: readonly T[]): T[];
  /** An independent stream derived from this one's seed and the label. */
  fork(label: string): Rng;
  /** The seed this stream was created with. Exposed so a run can record how to replay itself. */
  readonly seed: number;
}

/** sfc32 — small, fast, and passes PractRand. Chosen over an LCG for distribution quality. */
function sfc32(a: number, b: number, c: number, d: number): () => number {
  return function next(): number {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

/** Deterministic 32-bit string hash, used to derive fork seeds from labels. */
function hashLabel(seed: number, label: string): number {
  let h = seed ^ 0x9e3779b9;
  for (let i = 0; i < label.length; i++) {
    h ^= label.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

class Sfc32Rng implements Rng {
  readonly seed: number;
  private readonly gen: () => number;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    // Four distinct starting words from one seed, so nearby seeds do not produce nearby streams.
    const s = this.seed;
    this.gen = sfc32(s ^ 0x2f6e2b1, s ^ 0x9e3779b9, s ^ 0x85ebca6b, s ^ 0xc2b2ae35);
    // Discard the first outputs; sfc32's early values are correlated with the seed.
    for (let i = 0; i < 12; i++) this.gen();
  }

  next(): number {
    return this.gen();
  }

  int(maxExclusive: number): number {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
      throw new RangeError(`Rng.int requires a positive integer bound, got ${maxExclusive}`);
    }
    return Math.floor(this.next() * maxExclusive);
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('Rng.pick called on an empty array');
    return items[this.int(items.length)] as T;
  }

  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const a = out[i] as T;
      const b = out[j] as T;
      out[i] = b;
      out[j] = a;
    }
    return out;
  }

  fork(label: string): Rng {
    return new Sfc32Rng(hashLabel(this.seed, label));
  }
}

export function createRng(seed: number): Rng {
  return new Sfc32Rng(seed);
}

/** Only used when no seed is supplied by a test or a saved run. */
export const DEFAULT_SEED = 0x5eed;
